import type { LabPlan, LabStep, LabSubject } from '@/types/admin'

/**
 * Geometry for the scheduling-lab timeline, kept pure so it can be tested
 * without a DOM. Time runs left to right on a signed square-root scale: a plan
 * spans a first day and a second year, and a linear axis would crush the first
 * month — where every interesting decision happens — into a few pixels.
 * Strength (the interval a review earns) climbs on a log scale, as in the
 * ladder chart.
 */

export const W = 720
export const H = 380
export const PAD = { top: 26, right: 28, bottom: 42, left: 50 }
export const PLOT_W = W - PAD.left - PAD.right
export const PLOT_H = H - PAD.top - PAD.bottom
export const FLOOR_Y = PAD.top + PLOT_H

/** Reviews scheduled the same day would sit on top of each other; this keeps them apart. */
const MIN_GAP = 9
/** A word with a long history keeps its most recent reviews; older ones add nothing to a plan. */
export const MAX_HISTORY = 16

export type NodeKind = 'origin' | 'past' | 'plan'

export interface LabNode {
  key: string
  kind: NodeKind
  /** Position in `plan.steps` for a plan node, in the shown history for a past one. */
  index: number
  day: number
  /** The interval the review earned: how far ahead it sent the word. */
  interval: number
  rating: number | null
  scripted: boolean
  status: string | null
}

export interface Point {
  x: number
  y: number
}

export const lerp = (a: number, b: number, t: number) => a + (b - a) * t

function planNode(step: LabStep, index: number): LabNode {
  return {
    key: `s${index}`,
    kind: 'plan',
    index,
    day: step.day,
    interval: step.interval_days,
    rating: step.rating,
    scripted: step.scripted,
    status: step.status,
  }
}

/**
 * A hand-set strength has no history, so it gets one implied review: the one that
 * set its interval, that many days ago, which makes the word fall due today.
 */
export function buildNodes(plan: LabPlan, subject: LabSubject): LabNode[] {
  const shown = plan.history.slice(-MAX_HISTORY)
  const past: LabNode[] = shown.map((review, index) => ({
    key: `h${index}`,
    kind: 'past',
    index,
    day: review.day,
    interval: review.interval_days,
    rating: review.rating,
    scripted: false,
    status: null,
  }))
  const origin: LabNode[] =
    subject.kind === 'strength'
      ? [
          {
            key: 'origin',
            kind: 'origin',
            index: 0,
            day: -subject.from_days,
            interval: subject.from_days,
            rating: null,
            scripted: false,
            status: null,
          },
        ]
      : []
  return [...origin, ...past, ...plan.steps.map(planNode)]
}

export function baselineNodes(plan: LabPlan): LabNode[] {
  return plan.baseline.map(planNode)
}

const signedSqrt = (day: number) => Math.sign(day) * Math.sqrt(Math.abs(day))

export interface Scales {
  x: (day: number) => number
  y: (days: number) => number
  domain: [number, number]
  ceiling: number
}

export function scalesFor(nodes: LabNode[], ghost: LabNode[], mastery: number): Scales {
  const days = [...nodes, ...ghost].map((node) => node.day)
  const lo = signedSqrt(Math.min(0, ...days))
  const hi = signedSqrt(Math.max(14, ...days))
  const span = hi - lo || 1
  const padded: [number, number] = [lo - span * 0.03, hi + span * 0.05]
  const longest = Math.max(0, ...[...nodes, ...ghost].map((node) => node.interval))
  const ceiling = Math.max(mastery * 1.4, longest * 1.15, 30)
  return {
    domain: padded,
    ceiling,
    x: (day) => PAD.left + ((signedSqrt(day) - padded[0]) / (padded[1] - padded[0])) * PLOT_W,
    y: (interval) =>
      PAD.top + (1 - Math.log(Math.max(0, interval) + 1) / Math.log(ceiling + 1)) * PLOT_H,
  }
}

/** Node positions, nudged apart where reviews share a day (an Again repeated in-session). */
export function place(nodes: LabNode[], scales: Scales): Point[] {
  const points: Point[] = []
  nodes.forEach((node, i) => {
    const x = scales.x(node.day)
    const previous = points[i - 1]
    points.push({ x: previous ? Math.max(x, previous.x + MIN_GAP) : x, y: scales.y(node.interval) })
  })
  return points
}

/**
 * A smooth curve through the points: cubic segments with Catmull-Rom tangents.
 * Tangents are clamped to the floor so a lapse to zero days cannot overshoot the axis.
 */
export function curvePath(points: Point[], floorY = FLOOR_Y) {
  if (points.length === 0) return ''
  const floor = (y: number) => Math.min(y, floorY)
  let d = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] ?? points[i]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[i + 2] ?? p2
    const c1x = p1.x + (p2.x - p0.x) / 6
    const c1y = floor(p1.y + (p2.y - p0.y) / 6)
    const c2x = p2.x - (p3.x - p1.x) / 6
    const c2y = floor(p2.y - (p3.y - p1.y) / 6)
    d += ` C${c1x.toFixed(1)},${c1y.toFixed(1)} ${c2x.toFixed(1)},${c2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`
  }
  return d
}

/** The curve closed down to the floor, for the glow beneath it. */
export function areaPath(points: Point[]) {
  if (points.length < 2) return ''
  const first = points[0]
  const last = points[points.length - 1]
  return `${curvePath(points)} L${last.x.toFixed(1)},${FLOOR_Y} L${first.x.toFixed(1)},${FLOOR_Y} Z`
}

const X_TICKS: { day: number; label: string }[] = [
  { day: -730, label: '2y ago' },
  { day: -365, label: '1y ago' },
  { day: -90, label: '3mo ago' },
  { day: -30, label: '1mo ago' },
  { day: -7, label: '1w ago' },
  { day: 7, label: '1w' },
  { day: 30, label: '1mo' },
  { day: 90, label: '3mo' },
  { day: 180, label: '6mo' },
  { day: 365, label: '1y' },
  { day: 730, label: '2y' },
  { day: 1095, label: '3y' },
]

export const Y_TICKS: { days: number; label: string }[] = [
  { days: 1, label: '1d' },
  { days: 7, label: '1w' },
  { days: 30, label: '1mo' },
  { days: 90, label: '3mo' },
  { days: 365, label: '1y' },
]

/** Ticks inside the domain, dropping any that would crowd today's marker or a neighbour. */
export function timeTicks(scales: Scales) {
  const today = scales.x(0)
  const placed: { day: number; label: string; x: number }[] = []
  X_TICKS.forEach((tick) => {
    const x = scales.x(tick.day)
    if (x < PAD.left || x > PAD.left + PLOT_W) return
    if (Math.abs(x - today) < 40) return
    if (placed.some((p) => Math.abs(p.x - x) < 40)) return
    placed.push({ ...tick, x })
  })
  return placed
}

/** "in 3 weeks", "today", "5 days ago" — how far a review sits from now. */
export function relativeDay(day: number) {
  const rounded = Math.round(day)
  if (rounded === 0) return 'today'
  const abs = Math.abs(rounded)
  const unit =
    abs >= 730
      ? `${Math.round(abs / 365)} years`
      : abs >= 60
        ? `${Math.round(abs / 30)} months`
        : abs >= 14
          ? `${Math.round(abs / 7)} weeks`
          : abs === 1
            ? '1 day'
            : `${abs} days`
  return rounded > 0 ? `in ${unit}` : `${unit} ago`
}

/** A day count written the way a learner thinks about it: "12 d", "6 wk", "4 mo". */
export function spanLabel(days: number) {
  if (days < 1) return 'same day'
  if (days < 14) return `${Math.round(days)} d`
  if (days < 60) return `${Math.round(days / 7)} wk`
  if (days < 730) return `${Math.round(days / 30)} mo`
  return `${(days / 365).toFixed(1)} y`
}

export function calendarDate(day: number, now = new Date()) {
  const date = new Date(now.getTime() + day * 86_400_000)
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}
